import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Search, Loader2, X, Activity, Calendar, Download, Filter,
  Trash2, Pencil, Stethoscope, Receipt, TrendingDown, Award,
} from "lucide-react";
import Pagination from "@/components/Pagination";
import { inr } from "@/lib/format";
import { hasCap } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin · Visit Tracking
 * Reception flow: search/scan member → create visit → apply benefits.
 * Roles: staff + admin + super_admin (write); accounts (read).
 */

const STATUS_TONE = {
  "Checked In": "bg-[#FFF4DC] text-[#8C5A00]",
  "Completed": "bg-[#E5F2EA] text-[#1F7A3D]",
  "Cancelled": "bg-[#FCE9E6] text-[#A53A2A]",
};

export default function AdminVisits() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "admin", "staff"].includes(user?.role);
  const canDelete = ["super_admin", "admin"].includes(user?.role);
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState(() => ({
    q: searchParams.get("member_id") || "",
    department: "", visit_type: "", status: "", from_date: "", to_date: "",
  }));
  const [catalog, setCatalog] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);

  // Prefilter visits when arriving via /admin/visits?member_id=NPC001957
  useEffect(() => {
    const mid = searchParams.get("member_id");
    if (mid) setFilters((f) => ({ ...f, q: mid }));
  }, [searchParams]);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/visits", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPage(r.data.page || 1);
    } catch (e) { toast.error(apiErr(e, "Failed to load visits")); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/visits/meta/catalog").then((r) => setCatalog(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => { load(1); }, []);

  const onApplyFilters = () => load(1);
  const onResetFilters = () => { setFilters({ q: "", department: "", visit_type: "", status: "", from_date: "", to_date: "" }); setTimeout(() => load(1), 0); };

  const onExport = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && (k === "from_date" || k === "to_date")) params.set(k, v); });
      await downloadFile(`/visits/export.csv?${params.toString()}`, "npc-visits.csv");
    } catch (e) { toast.error(apiErr(e, "Export failed")); }
  };

  const onDelete = async (v) => {
    if (!window.confirm(`Delete visit on ${v.visit_date?.slice(0, 10)} for ${v.member_name}? Benefit usage will roll back.`)) return;
    try {
      await api.delete(`/visits/${v.visit_id}`);
      toast.success("Visit deleted");
      await load(page);
    } catch (e) { toast.error(apiErr(e, "Delete failed")); }
  };

  const stats = useMemo(() => {
    const billed = items.reduce((s, v) => s + (Number(v.bill_amount) || 0), 0);
    const saved = items.reduce((s, v) => s + (Number(v.total_savings) || 0), 0);
    return { billed, saved };
  }, [items]);

  return (
    <div data-testid="admin-visits-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0F2540] flex items-center gap-2">
            <Activity className="h-6 w-6 text-[#1E4F87]" />
            Visit Tracking
          </h1>
          <p className="text-sm text-[#5C6B7A] mt-1">Log every NPC member visit to measure engagement and benefit utilisation.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onExport} data-testid="visits-export-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#E1E8F0] text-[#0F2540] rounded-xl text-sm font-semibold hover:border-[#1E4F87]">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          {canWrite && (
            <button onClick={() => { setEditingVisit(null); setFormOpen(true); }} data-testid="visits-new-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E4F87] text-white rounded-xl text-sm font-semibold hover:bg-[#0F2540]">
              <Plus className="h-4 w-4" /> New Visit
            </button>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Visits (page)" value={items.length} sub={`of ${total} total`} icon={Activity} />
        <StatCard label="Billed (page)" value={inr(stats.billed)} sub="filtered" icon={Receipt} />
        <StatCard label="Savings (page)" value={inr(stats.saved)} sub="passed to members" icon={TrendingDown} tone="gold" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E1E8F0] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-[#5C6B7A]" />
          <span className="text-sm font-semibold text-[#0F2540]">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="col-span-2 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
            <input type="text" placeholder="Name / Member ID / NPC-M…" value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="visits-filter-q"
              className="w-full pl-9 pr-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
          </div>
          <select value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}
            data-testid="visits-filter-dept"
            className="px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
            <option value="">All departments</option>
            {(catalog.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filters.visit_type} onChange={(e) => setFilters({ ...filters, visit_type: e.target.value })}
            data-testid="visits-filter-type"
            className="px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
            <option value="">All types</option>
            {(catalog.visit_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
            <option value="">All statuses</option>
            {(catalog.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            className="px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
          <input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            className="px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={onApplyFilters} data-testid="visits-apply-filters"
            className="px-4 py-1.5 bg-[#0F2540] text-white rounded-xl text-xs font-semibold hover:bg-[#1E4F87]">Apply</button>
          <button onClick={onResetFilters}
            className="px-4 py-1.5 border border-[#E1E8F0] text-[#0F2540] rounded-xl text-xs font-semibold hover:border-[#1E4F87]">Reset</button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E1E8F0] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#5C6B7A]"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center" data-testid="visits-empty">
            <Stethoscope className="h-10 w-10 text-[#1E4F87] opacity-40 mx-auto mb-3" />
            <p className="text-sm text-[#5C6B7A]">No visits found. Click &quot;New Visit&quot; to log one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F9FC] text-[#5C6B7A] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Member</th>
                  <th className="text-left px-4 py-3">Department · Type</th>
                  <th className="text-left px-4 py-3">Benefits Redeemed</th>
                  <th className="text-right px-4 py-3">Bill</th>
                  <th className="text-right px-4 py-3">Saved</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.visit_id} className="border-t border-[#E1E8F0] hover:bg-[#FAFBFD]" data-testid={`visit-row-${v.visit_id}`}>
                    <td className="px-4 py-3 text-[#0F2540] whitespace-nowrap">
                      {v.visit_date ? new Date(v.visit_date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0F2540]">{v.member_name || "—"}</div>
                      <div className="text-xs text-[#5C6B7A]">{v.member_id} · {v.plan_name}</div>
                    </td>
                    <td className="px-4 py-3 text-[#0F2540]">
                      <div>{v.department}</div>
                      <div className="text-xs text-[#5C6B7A]">{v.visit_type}{v.doctor_name ? ` · ${v.doctor_name}` : ""}</div>
                    </td>
                    <td className="px-4 py-3" data-testid={`visit-row-${v.visit_id}-benefits`}>
                      {(v.benefits_applied || []).length === 0 ? (
                        <span className="text-xs text-[#5C6B7A]">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-[260px]">
                          {(v.benefits_applied || []).map((b, idx) => (
                            <span key={idx} title={`Saved ${inr(b.savings || 0)}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#FAF1DC] text-[#8C6A00] border border-[#F0D58A] rounded-full px-2 py-0.5">
                              <Award className="h-2.5 w-2.5" />
                              {b.title}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-[#0F2540] font-medium">{inr(v.bill_amount || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#1F7A3D]">{inr(v.total_savings || 0)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TONE[v.status] || "bg-[#F2F2F2] text-[#5C6B7A]"}`}>{v.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {canWrite && (
                        <button onClick={() => { setEditingVisit(v); setFormOpen(true); }}
                          data-testid={`visit-edit-${v.visit_id}`}
                          className="p-1.5 rounded-lg hover:bg-[#EAF1FA]">
                          <Pencil className="h-4 w-4 text-[#1E4F87]" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => onDelete(v)}
                          data-testid={`visit-delete-${v.visit_id}`}
                          className="p-1.5 rounded-lg hover:bg-[#FCE9E6] ml-1">
                          <Trash2 className="h-4 w-4 text-[#A53A2A]" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-[#E1E8F0] px-4 py-3">
          <Pagination page={page} total={total} limit={limit} onPage={(p) => load(p)} />
        </div>
      </div>

      {formOpen && (
        <VisitFormModal
          editing={editingVisit}
          catalog={catalog}
          onClose={() => { setFormOpen(false); setEditingVisit(null); }}
          onSaved={() => { setFormOpen(false); setEditingVisit(null); load(page); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, tone = "navy" }) {
  const toneCls = {
    navy: "bg-[#EAF1FA] text-[#1E4F87]",
    gold: "bg-[#FAF1DC] text-[#8C6A00]",
  }[tone];
  return (
    <div className="bg-white border border-[#E1E8F0] rounded-2xl p-4 flex items-center gap-4">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${toneCls}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-[#5C6B7A]">{label}</div>
        <div className="font-display text-xl font-bold text-[#0F2540]">{value}</div>
        {sub && <div className="text-xs text-[#5C6B7A]">{sub}</div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Visit form modal — search member → pick benefits → calc savings → save
// ------------------------------------------------------------------
function VisitFormModal({ editing, catalog, onClose, onSaved }) {
  const isEdit = !!editing;
  const [memberQuery, setMemberQuery] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [member, setMember] = useState(null); // {member_id, full_name, plan_id, plan_name, membership_id}
  const [wallet, setWallet] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(() => ({
    visit_date: new Date().toISOString().slice(0, 16),
    department: catalog.departments?.[0] || "OPD",
    doctor_name: "",
    visit_type: "OPD",
    bill_amount: 0,
    notes: "",
    status: "Completed",
    benefit_ids: [],
  }));

  // EDIT mode: pre-fill (no benefit reapply for safety)
  useEffect(() => {
    if (!editing) return;
    setForm({
      visit_date: (editing.visit_date || new Date().toISOString()).slice(0, 16),
      department: editing.department,
      doctor_name: editing.doctor_name || "",
      visit_type: editing.visit_type,
      bill_amount: editing.bill_amount || 0,
      notes: editing.notes || "",
      status: editing.status,
      benefit_ids: [],
    });
    setMember({
      member_id: editing.member_id,
      full_name: editing.member_name,
      plan_id: editing.plan_id,
      plan_name: editing.plan_name,
      membership_id: editing.membership_id,
    });
  }, [editing]);

  // Search members by name / id / membership number
  useEffect(() => {
    if (isEdit) return;
    if (!memberQuery || memberQuery.trim().length < 2) { setMemberResults([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/memberships", { params: { q: memberQuery, limit: 10 } });
        const list = r.data?.items || r.data || [];
        if (alive) setMemberResults(Array.isArray(list) ? list : []);
      } catch { /* ignore */ }
      finally { if (alive) setSearching(false); }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [memberQuery, isEdit]);

  const pickMember = (m) => {
    setMember({
      member_id: m.member_id,
      full_name: m.full_name,
      plan_id: m.plan_id,
      plan_name: m.plan_name,
      membership_id: m.membership_id,
    });
    setMemberResults([]);
    setMemberQuery("");
  };

  // Load wallet for picked member
  useEffect(() => {
    if (!member?.member_id || isEdit) { setWallet([]); return; }
    let alive = true;
    setWalletLoading(true);
    api.get(`/members/${member.member_id}/benefits`)
      .then((r) => { if (alive) setWallet(r.data?.benefits || []); })
      .catch(() => { if (alive) setWallet([]); })
      .finally(() => { if (alive) setWalletLoading(false); });
    return () => { alive = false; };
  }, [member?.member_id, isEdit]);

  const calcSavings = (b, bill) => {
    if (b.benefit_type === "free") return Number(bill || 0);
    if (b.benefit_type === "discount") {
      if (b.discount_type === "percent") return (Number(bill || 0) * (b.discount_value || 0)) / 100;
      if (b.discount_type === "amount") return Math.min(b.discount_value || 0, Number(bill || 0));
    }
    return 0;
  };

  const totalEstimatedSavings = useMemo(() => {
    const sum = form.benefit_ids.reduce((s, bid) => {
      const b = wallet.find((x) => x.benefit_id === bid);
      return s + (b ? calcSavings(b, form.bill_amount) : 0);
    }, 0);
    return Math.round(sum * 100) / 100;
  }, [form.benefit_ids, form.bill_amount, wallet]);

  const submit = async () => {
    if (!member?.member_id) { toast.error("Please pick a member"); return; }
    if (!form.department || !form.visit_type) { toast.error("Department & visit type required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/visits/${editing.visit_id}`, {
          visit_date: new Date(form.visit_date).toISOString(),
          department: form.department,
          doctor_name: form.doctor_name,
          visit_type: form.visit_type,
          bill_amount: Number(form.bill_amount) || 0,
          notes: form.notes,
          status: form.status,
        });
      } else {
        await api.post("/visits", {
          member_id: member.member_id,
          visit_date: new Date(form.visit_date).toISOString(),
          department: form.department,
          doctor_name: form.doctor_name,
          visit_type: form.visit_type,
          bill_amount: Number(form.bill_amount) || 0,
          notes: form.notes,
          status: form.status,
          benefit_ids: form.benefit_ids,
        });
      }
      toast.success(isEdit ? "Visit updated" : "Visit saved");
      onSaved();
    } catch (e) { toast.error(apiErr(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="visit-form-modal">
        <div className="sticky top-0 bg-white border-b border-[#E1E8F0] px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-[#0F2540]">{isEdit ? "Edit Visit" : "New Visit"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F2F2F2]"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Member picker (create mode only) */}
          {!isEdit && (
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Member *</label>
              {!member ? (
                <div>
                  <div className="relative mt-1">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
                    <input type="text" placeholder="Search by name / NPC ID / membership #" value={memberQuery}
                      onChange={(e) => setMemberQuery(e.target.value)}
                      data-testid="visit-form-member-search"
                      autoFocus
                      className="w-full pl-9 pr-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
                  </div>
                  {searching && <div className="mt-2 text-xs text-[#5C6B7A]"><Loader2 className="h-3 w-3 inline animate-spin" /> Searching…</div>}
                  {memberResults.length > 0 && (
                    <div className="mt-2 border border-[#E1E8F0] rounded-xl divide-y divide-[#E1E8F0] max-h-64 overflow-y-auto">
                      {memberResults.map((m) => (
                        <button key={m.membership_id} onClick={() => pickMember(m)}
                          data-testid={`visit-form-member-pick-${m.member_id}`}
                          className="w-full text-left px-4 py-2 hover:bg-[#FAFBFD]">
                          <div className="font-semibold text-[#0F2540]">{m.full_name}</div>
                          <div className="text-xs text-[#5C6B7A]">{m.member_id} · {m.plan_name} · {m.membership_number}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-1 flex items-center justify-between bg-[#EAF1FA] rounded-xl px-4 py-3" data-testid="visit-form-member-picked">
                  <div>
                    <div className="font-semibold text-[#0F2540]">{member.full_name}</div>
                    <div className="text-xs text-[#5C6B7A]">{member.member_id} · {member.plan_name}</div>
                  </div>
                  <button onClick={() => { setMember(null); setForm({ ...form, benefit_ids: [] }); }}
                    className="text-xs text-[#A53A2A] font-semibold hover:underline">Change</button>
                </div>
              )}
            </div>
          )}

          {isEdit && member && (
            <div className="bg-[#EAF1FA] rounded-xl px-4 py-3">
              <div className="font-semibold text-[#0F2540]">{member.full_name}</div>
              <div className="text-xs text-[#5C6B7A]">{member.member_id} · {member.plan_name}</div>
            </div>
          )}

          {/* Visit details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Visit Date & Time *</label>
              <input type="datetime-local" value={form.visit_date}
                onChange={(e) => setForm({ ...form, visit_date: e.target.value })}
                data-testid="visit-form-date"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Visit Type *</label>
              <select value={form.visit_type} onChange={(e) => setForm({ ...form, visit_type: e.target.value })}
                data-testid="visit-form-type"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
                {(catalog.visit_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Department *</label>
              <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
                data-testid="visit-form-department"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
                {(catalog.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Doctor</label>
              <input type="text" value={form.doctor_name} onChange={(e) => setForm({ ...form, doctor_name: e.target.value })}
                placeholder="Dr. Name"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Bill Amount (₹)</label>
              <input type="number" min="0" step="0.01" value={form.bill_amount}
                onChange={(e) => setForm({ ...form, bill_amount: e.target.value })}
                data-testid="visit-form-bill"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
                {(catalog.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Benefit picker — only on CREATE */}
          {!isEdit && member && (
            <div className="rounded-2xl border-2 border-[#F0D58A] bg-gradient-to-br from-[#FFF7E6] to-white p-4" data-testid="visit-form-wallet">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-[#A0741F]" />
                  <div>
                    <div className="text-sm font-bold text-[#0F2540]">Apply benefits from member&apos;s wallet</div>
                    <div className="text-[11px] text-[#5C6B7A]">Each selected benefit is automatically redeemed (1 use deducted) when you save this visit.</div>
                  </div>
                </div>
                {form.benefit_ids.length > 0 && (
                  <span className="text-xs font-semibold text-[#1F7A3D] bg-[#E5F2EA] px-2.5 py-1 rounded-full" data-testid="visit-form-applied-count">
                    {form.benefit_ids.length} selected
                  </span>
                )}
              </div>

              {walletLoading ? (
                <div className="text-xs text-[#5C6B7A] py-3"><Loader2 className="h-3 w-3 inline animate-spin" /> Loading wallet…</div>
              ) : wallet.length === 0 ? (
                <div className="text-xs text-[#A53A2A] bg-[#FCE9E6] rounded-lg px-3 py-2">
                  No active benefits attached to this plan. Add benefits at Admin → Plan Benefits and attach them to <strong>{member.plan_name}</strong>.
                </div>
              ) : (
                <div className="space-y-2">
                  {wallet.map((b) => {
                    const exhausted = b.total_allowed != null && b.remaining_count <= 0;
                    const checked = form.benefit_ids.includes(b.benefit_id);
                    const savings = calcSavings(b, form.bill_amount);
                    const remainingBefore = b.remaining_count;
                    const remainingAfter = b.total_allowed == null
                      ? null
                      : Math.max(0, (b.remaining_count ?? b.total_allowed) - (checked ? 1 : 0));
                    return (
                      <label
                        key={b.benefit_id}
                        className={`flex items-center justify-between gap-3 p-3 rounded-xl border cursor-pointer transition ${exhausted ? "opacity-60 cursor-not-allowed bg-[#F7F9FC]" : ""} ${checked ? "border-[#1F7A3D] bg-[#E5F2EA]" : "border-[#E1E8F0] bg-white"}`}
                      >
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={exhausted}
                            data-testid={`visit-form-benefit-${b.benefit_id}`}
                            onChange={() => {
                              const cur = new Set(form.benefit_ids);
                              if (cur.has(b.benefit_id)) cur.delete(b.benefit_id);
                              else cur.add(b.benefit_id);
                              setForm({ ...form, benefit_ids: Array.from(cur) });
                            }}
                            className="mt-1 h-4 w-4 accent-[#1F7A3D]"
                          />
                          <div className="min-w-0">
                            <div className="font-semibold text-[#0F2540] text-sm">{b.title}</div>
                            <div className="text-[11px] text-[#5C6B7A] mt-0.5">
                              {b.category} · {b.reset_period.replace("_", " ")} ·{" "}
                              {b.benefit_type === "discount"
                                ? (b.discount_type === "percent" ? `${b.discount_value}% off` : `₹${b.discount_value} off`)
                                : "Free (full bill waived)"}
                            </div>
                            {/* Balance ticker — always visible */}
                            <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold">
                              {b.total_allowed == null ? (
                                <span className="bg-[#EAF1FA] text-[#1E4F87] rounded-full px-2 py-0.5">UNLIMITED</span>
                              ) : (
                                <>
                                  <span className="bg-[#EAF1FA] text-[#1E4F87] rounded-full px-2 py-0.5">
                                    {remainingBefore} / {b.total_allowed} left
                                  </span>
                                  {checked && remainingAfter !== null && (
                                    <>
                                      <span className="text-[#5C6B7A]">→</span>
                                      <span className="bg-[#1F7A3D] text-white rounded-full px-2 py-0.5">
                                        {remainingAfter} / {b.total_allowed} after save
                                      </span>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          {checked && savings > 0 && (
                            <div className="text-sm font-bold text-[#1F7A3D]">−{inr(savings)}</div>
                          )}
                          {exhausted && (
                            <div className="text-[10px] font-semibold text-[#A53A2A]">EXHAUSTED</div>
                          )}
                          {!checked && !exhausted && savings > 0 && (
                            <div className="text-[11px] text-[#5C6B7A]">Would save {inr(savings)}</div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Bill ↔ Savings ↔ Net summary — visible whenever a bill or benefit is set */}
              {(Number(form.bill_amount) > 0 || form.benefit_ids.length > 0) && (
                <div className="mt-3 rounded-xl bg-white border border-[#E1E8F0] p-3" data-testid="visit-form-summary">
                  <div className="grid grid-cols-3 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5C6B7A]">Bill</div>
                      <div className="font-display text-lg font-bold text-[#0F2540]">{inr(Number(form.bill_amount) || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5C6B7A]">Member saves</div>
                      <div className="font-display text-lg font-bold text-[#1F7A3D]">−{inr(totalEstimatedSavings)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5C6B7A]">Member pays</div>
                      <div className="font-display text-lg font-bold text-[#0F2540]">
                        {inr(Math.max(0, (Number(form.bill_amount) || 0) - totalEstimatedSavings))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E1E8F0] px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E1E8F0] rounded-xl hover:border-[#1E4F87]">Cancel</button>
          <button onClick={submit} disabled={saving || (!isEdit && !member?.member_id)} data-testid="visit-form-save"
            className="px-4 py-2 text-sm bg-[#1E4F87] text-white rounded-xl font-semibold hover:bg-[#0F2540] disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save visit
          </button>
        </div>
      </div>
    </div>
  );
}
